/**
 * Rejects server function requests that do not speak the current RPC protocol.
 *
 * Since TanStack Start 1.170 the server treats a `/_serverFn/*` request as an
 * RPC call only when it carries `x-tsr-serverFn: true`. Without the header it
 * hands back the handler's raw return value instead of an HTTP response, and
 * the request ends as a 500 ("Internal Server Error", Sentry DCM-VN) after
 * Clerk's middleware has already crashed appending headers to the missing
 * response (Sentry DCM-VP).
 *
 * Two kinds of caller send no header: browser tabs still running a bundle
 * from before the 1.170 upgrade (the 1.141 client never sent it) and crawlers
 * that follow server function URLs. Neither can use the answer, so they get a
 * plain 400 asking for a reload. The old client throws that text as the
 * error message, and the request never reaches Clerk or the handler.
 *
 * Aliases: server fn version skew, stale client, x-tsr-serverFn guard.
 */

import { createMiddleware } from "@tanstack/react-start";

export const SERVER_FN_HEADER = "x-tsr-serverFn";

export const STALE_CLIENT_MESSAGE =
  "This page is out of date. Please reload it to continue.";

/** Returns the response to send instead of running the server function. */
export function getServerFnProtocolRejection(
  request: Request,
): Response | undefined {
  if (request.headers.get(SERVER_FN_HEADER) === "true") return undefined;
  return new Response(STALE_CLIENT_MESSAGE, {
    status: 400,
    headers: {
      "Content-Type": "text/plain; charset=utf-8",
      "Cache-Control": "no-store",
    },
  });
}

export const serverFnProtocolMiddleware = createMiddleware().server(
  ({ request, handlerType, next }) => {
    if (handlerType !== "serverFn") return next();
    return getServerFnProtocolRejection(request) ?? next();
  },
);
